/**
 * Stacked participant cards with toolbar — edit-appointment modal layout.
 * See design system → ParticipantCardsPanel.
 */
import {
  ActionIcon,
  Autocomplete,
  Box,
  Button,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconCalendarEvent, IconPlus, IconStarFilled, IconX } from '@tabler/icons-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ParticipantAttendanceSelect } from '@/components/ParticipantAttendanceSelect';
import { CARD_TABLE_PAPER_PROPS } from '@/components/unifyTableVariants';
import type { AppointmentModality } from '@/features/calendar/AppointmentSharedFields';
import {
  locationRequiredForClientAttendance,
  type CalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';
import type { ParticipantAttendanceFhirStatus } from '@/pages/appointments/utils/participantAttendance';

export interface AdHocParticipantInput {
  name: string;
  email: string;
  phone?: string;
}

export interface ParticipantCardRow {
  id: string;
  participantRef?: string;
  name: string;
  profileHref?: string;
  role?: string;
  email?: string;
  phone?: string;
  modality?: string;
  location?: string;
  attendanceStatus?: string;
  isSubject?: boolean;
}

export interface ParticipantCardsPanelProps {
  rows: ParticipantCardRow[];
  title?: string;
  participantOptions?: { value: string; label: string }[];
  onAdd?: (participantRef: string) => void;
  /** Create / reuse a RelatedPerson by name + email and add them to the appointment. */
  onAddAdHoc?: (input: AdHocParticipantInput) => void | Promise<void>;
  onRemove?: (participantRef: string) => void;
  addDisabled?: boolean;
  onAttendanceChange?: (participantId: string, status: ParticipantAttendanceFhirStatus) => void;
  savedStatusByParticipantId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  onLocationChange?: (participantId: string, location: string | undefined) => void;
  locationOptionsByParticipantId?: Record<string, string[]>;
  sessionModality?: AppointmentModality;
  onModalityChange?: (modality: AppointmentModality) => void;
  modalityOptions?: { value: string; label: string }[];
  emptyMessage?: string;
}

function DetailField({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <Stack gap={4} style={{ minWidth: 0 }}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

/** Isolated so keystrokes do not re-render the participant cards list. */
function AdHocParticipantAddForm({
  onSubmit,
  onBack,
  showBack,
}: {
  onSubmit: (input: AdHocParticipantInput) => void | Promise<void>;
  onBack?: () => void;
  showBack?: boolean;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (): Promise<void> => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName) {
      setError('Enter a name');
      return;
    }
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSubmit({
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add participant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="xs">
      {showBack && onBack ? (
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          onClick={onBack}
          style={{ alignSelf: 'flex-start' }}
        >
          Back to contacts
        </Button>
      ) : null}
      <Group align="flex-end" grow preventGrowOverflow={false} wrap="wrap">
        <TextInput
          label="Name"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          disabled={saving}
        />
        <TextInput
          label="Email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          disabled={saving}
        />
        <TextInput
          label="Phone"
          placeholder="Optional"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          disabled={saving}
        />
        <Button
          color="gold"
          loading={saving}
          onClick={() => void submit()}
          style={{ flex: '0 0 auto' }}
        >
          Add person
        </Button>
      </Group>
      {error ? (
        <Text size="xs" c="red">
          {error}
        </Text>
      ) : null}
    </Stack>
  );
}

export function ParticipantCardsPanel({
  rows,
  title = 'Participants',
  participantOptions = [],
  onAdd,
  onAddAdHoc,
  onRemove,
  addDisabled = false,
  onAttendanceChange,
  savedStatusByParticipantId = {},
  onLocationChange,
  locationOptionsByParticipantId = {},
  sessionModality,
  onModalityChange,
  modalityOptions = [],
  emptyMessage = 'No participants',
}: ParticipantCardsPanelProps): JSX.Element {
  const showSubjectIndicator = rows.some((row) => row.isSubject);
  const [adding, setAdding] = useState(false);
  const [showAdHocForm, setShowAdHocForm] = useState(false);
  const addSearchRef = useRef<HTMLInputElement>(null);
  const canAdd = (Boolean(onAdd) || Boolean(onAddAdHoc)) && !addDisabled;
  const showExistingPicker = Boolean(onAdd) && participantOptions.length > 0;
  const adHocFormVisible = Boolean(onAddAdHoc) && (showAdHocForm || !showExistingPicker);

  useEffect(() => {
    if (adding && showExistingPicker && !showAdHocForm) addSearchRef.current?.focus();
  }, [adding, showExistingPicker, showAdHocForm]);

  useEffect(() => {
    if (!canAdd) setAdding(false);
  }, [canAdd]);

  useEffect(() => {
    if (!adding) setShowAdHocForm(false);
  }, [adding]);

  return (
    <Paper {...CARD_TABLE_PAPER_PROPS}>
      <Group
        justify="space-between"
        align="center"
        px="md"
        py="sm"
        wrap="nowrap"
        style={{ borderBottom: adding ? undefined : '1px solid var(--mantine-color-gray-3)' }}
      >
        <Text size="sm" fw={600}>
          {title}
        </Text>
        {canAdd ? (
          <Button
            size="xs"
            variant={adding ? 'subtle' : 'light'}
            color="gold"
            leftSection={<IconPlus size={14} />}
            onClick={() => setAdding((open) => !open)}
          >
            {adding ? 'Cancel' : 'Add'}
          </Button>
        ) : null}
      </Group>
      {adding && canAdd ? (
        <Stack
          gap="sm"
          px="md"
          pb="sm"
          style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
        >
          {showExistingPicker && !showAdHocForm ? (
            <>
              <Select
                ref={addSearchRef}
                placeholder="Search people to add"
                data={participantOptions}
                searchable
                nothingFoundMessage="No matching people"
                comboboxProps={{ withinPortal: false }}
                onChange={(participantRef) => {
                  if (!participantRef) return;
                  onAdd?.(participantRef);
                  setAdding(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setAdding(false);
                }}
              />
              {onAddAdHoc ? (
                <Button
                  size="xs"
                  variant="subtle"
                  color="gold"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setShowAdHocForm(true)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add someone new
                </Button>
              ) : null}
            </>
          ) : null}
          {adHocFormVisible && onAddAdHoc ? (
            <AdHocParticipantAddForm
              key={showAdHocForm ? 'adhoc' : 'adhoc-solo'}
              showBack={showExistingPicker && showAdHocForm}
              onBack={() => setShowAdHocForm(false)}
              onSubmit={async (input) => {
                await onAddAdHoc(input);
                setAdding(false);
              }}
            />
          ) : null}
        </Stack>
      ) : null}
      {rows.length === 0 ? (
        <Box px="md" py="lg">
          <Text size="sm" c="dimmed">
            {emptyMessage}
          </Text>
        </Box>
      ) : (
        <Stack gap={0}>
          {rows.map((row, index) => {
            const saved = savedStatusByParticipantId[row.id];
            const attendanceValue =
              (row.attendanceStatus as CalendarClientAttendanceStatus | undefined)
              ?? saved
              ?? 'scheduled';
            return (
              <Box
                key={row.id}
                px="md"
                py="md"
                style={{
                  borderBottom:
                    index < rows.length - 1 ? '1px solid var(--mantine-color-gray-3)' : undefined,
                }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap" mb="md">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <ThemeIcon variant="light" color="gold" radius="md" size={40}>
                      <IconCalendarEvent size={20} />
                    </ThemeIcon>
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap={6} wrap="nowrap">
                        {showSubjectIndicator && row.isSubject ? (
                          <IconStarFilled
                            size={12}
                            color="var(--mantine-color-gold-6)"
                            aria-label="Subject client"
                          />
                        ) : null}
                        {row.profileHref ? (
                          <Text
                            component={Link}
                            to={row.profileHref}
                            size="sm"
                            fw={600}
                            c="gold.7"
                            truncate
                            style={{ textDecoration: 'none' }}
                          >
                            {row.name}
                          </Text>
                        ) : (
                          <Text size="sm" fw={600} truncate>
                            {row.name}
                          </Text>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {row.role ?? 'Client'}
                      </Text>
                    </Stack>
                  </Group>
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <ParticipantAttendanceSelect
                      value={attendanceValue}
                      savedStatus={saved ?? 'scheduled'}
                      onChange={
                        onAttendanceChange
                          ? (status) => onAttendanceChange(row.id, status)
                          : undefined
                      }
                      readOnly={!onAttendanceChange}
                      aria-label={`Attendance for ${row.name}`}
                    />
                    {onRemove && row.participantRef && !row.isSubject ? (
                      <Tooltip label={`Remove ${row.name}`}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${row.name}`}
                          onClick={() => onRemove(row.participantRef as string)}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Group>
                </Group>
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                  <DetailField label="Email">
                    <Text size="sm" fw={500} truncate="end" title={row.email}>
                      {row.email || '—'}
                    </Text>
                  </DetailField>
                  <DetailField label="Phone">
                    <Text size="sm" fw={500} truncate="end" title={row.phone}>
                      {row.phone || '—'}
                    </Text>
                  </DetailField>
                  <DetailField label="Modality">
                    {onModalityChange && sessionModality ? (
                      <Select
                        size="sm"
                        data={modalityOptions}
                        value={sessionModality}
                        onChange={(next) => {
                          if (!next) return;
                          onModalityChange(next as AppointmentModality);
                        }}
                        allowDeselect={false}
                        aria-label={`Modality for ${row.name}`}
                      />
                    ) : (
                      <Text size="sm" fw={500}>
                        {row.modality || '—'}
                      </Text>
                    )}
                  </DetailField>
                  <DetailField label={locationRequiredForClientAttendance(attendanceValue) ? 'Location *' : 'Location'}>
                    {onLocationChange ? (
                      <Autocomplete
                        size="sm"
                        data={[
                          ...new Set([
                            ...(locationOptionsByParticipantId[row.id] ?? []),
                            ...(row.location?.trim() ? [row.location.trim()] : []),
                          ]),
                        ]}
                        value={row.location ?? ''}
                        placeholder={
                          locationRequiredForClientAttendance(attendanceValue)
                            ? 'Required when attended'
                            : 'Optional'
                        }
                        required={locationRequiredForClientAttendance(attendanceValue)}
                        clearable
                        aria-label={`Location for ${row.name}`}
                        onChange={(v) =>
                          onLocationChange(row.id, v.trim() ? v.trim() : undefined)
                        }
                      />
                    ) : (
                      <Text size="sm" fw={500}>
                        {row.location || '—'}
                      </Text>
                    )}
                  </DetailField>
                </SimpleGrid>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
